import {
  cloneElement,
  isValidElement,
  useId,
  useState,
  type AriaAttributes,
  type CSSProperties,
  type InputHTMLAttributes,
  type ReactNode,
  type TextareaHTMLAttributes,
} from "react";

import { sx } from "../style";

export interface FieldProps {
  label?: ReactNode;
  required?: boolean;
  helper?: ReactNode;
  error?: ReactNode;
  htmlFor?: string;
  children?: ReactNode;
  style?: CSSProperties;
}

/** Field wrapper: label + control + helper/error text. Use around any input. */
export function Field({ label, required, helper, error, htmlFor, children, style }: FieldProps) {
  // TD-0058: асоціюємо helper/error з контролом (aria-describedby), позначаємо
  // aria-invalid/aria-required і дублюємо помилку не лише кольором (іконка + role=alert).
  const descId = useId();
  const hasDesc = Boolean(error || helper);
  const extra: AriaAttributes = {};
  if (hasDesc) extra["aria-describedby"] = descId;
  if (error) extra["aria-invalid"] = true;
  if (required) extra["aria-required"] = true;
  const control = isValidElement(children) ? cloneElement(children, extra) : children;
  return (
    <div className="cf-field" style={sx({ display: "flex", flexDirection: "column", gap: 6, ...style })}>
      {label && (
        <label
          htmlFor={htmlFor}
          style={sx({
            fontSize: "var(--fs-label)",
            lineHeight: "var(--lh-label)",
            fontWeight: "var(--fw-medium)",
            color: "var(--color-text-primary)",
          })}
        >
          {label}
          {/* TD-0058: «обов'язковість» оголошується через aria-required на контролі
              (cloneElement нижче); зірочка лишається суто візуальною (aria-hidden),
              щоб не змінювати accessible name лейбла (інакше ламає getByLabelText). */}
          {required && (
            <span aria-hidden="true" style={{ color: "var(--color-status-error)", marginLeft: 2 }}>
              *
            </span>
          )}
        </label>
      )}
      {control}
      {error ? (
        <span
          id={descId}
          role="alert"
          style={sx({ fontSize: "var(--fs-helper)", lineHeight: "var(--lh-helper)", color: "var(--color-status-error)" })}
        >
          <span aria-hidden="true">⚠ </span>
          {error}
        </span>
      ) : (
        helper && (
          <span id={descId} style={sx({ fontSize: "var(--fs-helper)", lineHeight: "var(--lh-helper)", color: "var(--color-text-secondary)" })}>
            {helper}
          </span>
        )
      )}
    </div>
  );
}

function baseControl(focused: boolean, error?: boolean, disabled?: boolean): CSSProperties {
  return sx({
    width: "100%",
    height: 38,
    padding: "0 12px",
    fontSize: "var(--fs-body)",
    fontFamily: "var(--font-sans)",
    color: "var(--color-text-primary)",
    background: disabled ? "var(--neutral-50)" : "var(--neutral-0)",
    border: `1px solid ${error ? "var(--color-status-error)" : focused ? "var(--color-border-focus)" : "var(--color-border-default)"}`,
    borderRadius: "var(--radius-md)",
    outline: "none",
    boxShadow: focused ? (error ? "0 0 0 3px var(--color-status-error-bg)" : "var(--shadow-focus)") : "none",
    transition: "border-color var(--duration-fast), box-shadow var(--duration-fast)",
    opacity: disabled ? "var(--opacity-disabled)" : 1,
  });
}

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "style"> {
  error?: boolean;
  iconLeft?: ReactNode;
  mono?: boolean;
  style?: CSSProperties;
}

/** Single-line text input. Pass `iconLeft` to prefix an icon. */
export function Input({ type = "text", disabled, error, iconLeft, mono, style, className, ...rest }: InputProps) {
  const [focused, setFocused] = useState(false);
  const ctl = { ...baseControl(focused, error, disabled) };
  if (mono) {
    ctl.fontFamily = "var(--font-mono)";
    ctl.fontVariantNumeric = "tabular-nums";
  }
  const handlers = { onFocus: () => setFocused(true), onBlur: () => setFocused(false) };
  if (iconLeft) {
    return (
      <div className="cf-control-shell" style={{ position: "relative", ...style }}>
        <span
          style={{
            position: "absolute",
            left: 11,
            top: "50%",
            transform: "translateY(-50%)",
            color: "var(--color-text-tertiary)",
            display: "flex",
          }}
        >
          {iconLeft}
        </span>
        <input className={["cf-control", className].filter(Boolean).join(" ")} type={type} disabled={disabled} {...handlers} style={{ ...ctl, paddingLeft: 36 }} {...rest} />
      </div>
    );
  }
  return <input className={["cf-control", className].filter(Boolean).join(" ")} type={type} disabled={disabled} {...handlers} style={{ ...ctl, ...style }} {...rest} />;
}

export interface TextareaProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "style"> {
  error?: boolean;
  style?: CSSProperties;
}

/** Multi-line text input. */
export function Textarea({ disabled, error, rows = 4, style, className, ...rest }: TextareaProps) {
  const [focused, setFocused] = useState(false);
  return (
    <textarea
      className={["cf-control", "cf-textarea", className].filter(Boolean).join(" ")}
      disabled={disabled}
      rows={rows}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      style={{
        ...baseControl(focused, error, disabled),
        height: "auto",
        padding: "9px 12px",
        lineHeight: "var(--lh-body)" as unknown as number,
        resize: "vertical",
        ...style,
      }}
      {...rest}
    />
  );
}
